'use client'

import dynamic from 'next/dynamic'
import { useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import { useIntersectionObserver, useLayout } from '../hooks'

function injectImagePriority(html: string): string {
  return html.replace(/<img(\s)/i, '<img fetchpriority="high" loading="eager"$1')
}

function extractImageSrc(html: string): string | null {
  return html.match(/<img[^>]+src="([^"]+)"/i)?.[1] ?? null
}

const BannerflowBannerParser = dynamic(() =>
  import('./bannerflow-banner-parser').then(mod => mod.BannerflowBannerParser)
)

type ScriptContent = {
  script?: string | null
  scriptMobile?: string | null
}
type ImageTagContent = {
  dynamicImage?: string
  dynamicImageMobile?: string
}

type BannerContent = {
  properties: ScriptContent & ImageTagContent
  type: string
}

type Props = ScriptContent & {
  bordered?: boolean
  content?: Array<BannerContent>
  priority?: boolean
}

function parseScriptSrc(script: string): string {
  if (script.startsWith('<script')) {
    return script.match(/src="([^"]*)"/i)?.[1] || ''
  } else {
    return script
  }
}

export const BannerflowBanner = ({
  script: upLevelScript,
  scriptMobile: upLevelScriptMobile,
  content,
  bordered,
  priority = true,
}: Props) => {
  const { ref: containerRef, isVisible } = useIntersectionObserver({})
  const { responsive } = useLayout()

  const {
    dynamicImage: dynamicImageDesktop,
    scriptMobile: mobileScript,
    dynamicImageMobile,
    script,
  } = content?.[0]?.properties || {}
  const scriptDesktop = upLevelScript || script
  const scriptMobile = upLevelScriptMobile || mobileScript
  const isMobile = responsive.breakpoints.max.SM
  const reloadKey = `${responsive.breakpoints.min.SM}`

  const bannerScript = isMobile ? scriptMobile : scriptDesktop
  const bannerDynamicImage = isMobile ? dynamicImageMobile : dynamicImageDesktop

  // Preload the hero image as early as possible
  const imageSrc = bannerDynamicImage ? extractImageSrc(bannerDynamicImage) : null
  if (imageSrc && priority) {
    ReactDOM.preload(imageSrc, { as: 'image' })
  }

  useEffect(() => {
    if (scriptDesktop && scriptMobile) {
      const triggerClick = (target: HTMLDivElement) => {
        const currentIframe = target?.querySelector('iframe')
        const dataBannerTarget = currentIframe?.contentDocument?.body.querySelector(
          '[data-banner]'
        ) as HTMLDivElement

        dataBannerTarget?.click()
      }

      const clickEvent = ({ target }: MouseEvent) => {
        if (!target) {
          return
        }

        triggerClick(target as HTMLDivElement)
      }

      const keyPressEvent = ({ key, target }: KeyboardEvent) => {
        if (!target || key !== 'Enter') {
          return
        }

        triggerClick(target as HTMLDivElement)
      }

      const container = containerRef.current

      container?.addEventListener('click', clickEvent)
      container?.addEventListener('keypress', keyPressEvent)

      return () => {
        container?.removeEventListener('click', clickEvent)
        container?.removeEventListener('keypress', keyPressEvent)
      }
    }
  }, [containerRef, scriptDesktop, scriptMobile])

  const getBanner = (
    script: string | undefined | null,
    dynamicImage: string | undefined,
    reloadKey: string
  ) => {
    return dynamicImage ? (
      <div
        dangerouslySetInnerHTML={{
          __html: priority ? injectImagePriority(dynamicImage) : dynamicImage,
        }}
      />
    ) : (
      script && <BannerflowBannerParser async src={parseScriptSrc(script)} key={reloadKey} />
    )
  }

  const banner = useMemo(
    () => (priority || isVisible) && getBanner(bannerScript, bannerDynamicImage, reloadKey),
    [bannerDynamicImage, bannerScript, isVisible, priority, reloadKey]
  )
  return (
    <div
      className={classNames('c-bannerflowbanner', {
        'c-bannerflowbanner--bordered': bordered,
      })}>
      <div
        className={classNames('c-bannerflowbanner__wrapper', {
          'c-bannerflowbanner--mobile': isMobile,
          'c-bannerflowbanner--desktop': !isMobile,
        })}>
        <div className="c-bannerflowbanner__container" ref={containerRef}>
          {banner ?? null}
        </div>
      </div>
    </div>
  )
}
